import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  type AuthRoot,
  type AuthState,
  type AuthTokens,
  type SessionEndReason,
} from './auth.types';

const initialState: AuthState = { accessToken: null, refreshToken: null };

/**
 * The session, and nothing else.
 *
 * The reducer is pure: persistence is a `store.subscribe` side effect in
 * `createAppStore`, not a write from in here. Keeping it out means the slice can
 * be exercised without a DOM, and it means there is exactly one place that
 * decides what survives a reload.
 */
const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loggedIn(_state, action: PayloadAction<AuthTokens>): AuthState {
      return { ...action.payload };
    },
    /**
     * Both tokens are replaced, never just the access token: refresh rotation is
     * single-use, so the token that was just spent is already revoked server-side
     * and keeping it would guarantee the NEXT refresh looks like reuse — which
     * the api treats as theft and answers by revoking every session the actor has
     * (P2's `consumeRefresh`).
     */
    tokensRefreshed(_state, action: PayloadAction<AuthTokens>): AuthState {
      return { ...action.payload };
    },
    loggedOut(_state, action: PayloadAction<SessionEndReason | undefined>): AuthState {
      return action.payload === undefined
        ? { ...initialState }
        : { ...initialState, endedReason: action.payload };
    },
  },
});

export const { loggedIn, tokensRefreshed, loggedOut } = slice.actions;
export const authReducer = slice.reducer;

export const selectAccessToken = (state: AuthRoot): string | null => state.auth.accessToken;
export const selectRefreshToken = (state: AuthRoot): string | null => state.auth.refreshToken;

/**
 * Both tokens, deliberately. An access token with no refresh token is a session
 * that ends silently at its first 401 with no way to recover, so it is treated
 * as no session at all and the operator is sent to sign in while they still have
 * an unsaved form they can copy out of.
 */
export const selectIsAuthenticated = (state: AuthRoot): boolean =>
  state.auth.accessToken !== null && state.auth.refreshToken !== null;

export const selectSessionEndReason = (state: AuthRoot): SessionEndReason | null =>
  state.auth.endedReason ?? null;
